import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { plansAPI } from "../../services/transactionService";
import { useUser } from "../../context/UserContext";

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useUser();

  useEffect(() => {
    fetchPlans();
  }, []);

  // Handle redirect back from sign in with selected plan
  useEffect(() => {
    if (isAuthenticated) {
      const selectedPlanId = sessionStorage.getItem('selectedPlanId');
      if (selectedPlanId) {
        // Clear the stored plan ID
        sessionStorage.removeItem('selectedPlanId');
        // Automatically trigger subscription for the selected plan
        handleSubscribe(selectedPlanId);
      }
    }
  }, [isAuthenticated]);

  const fetchPlans = async () => {
    try {
      console.log("🔍 Fetching plans...");
      const response = await plansAPI.getPlans();
      console.log("🔍 Plans response:", response.data);
      setPlans(response.data);
    } catch (error) {
      console.error("❌ Error fetching plans:", error);
      toast.error("Failed to load plans. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      // Store the plan ID in sessionStorage to redirect back after sign in
      sessionStorage.setItem('selectedPlanId', planId);
      // Redirect to sign in page
      navigate('/signIn', { state: { from: '/plans' } });
      return;
    }

    setSubscribing(planId);
    try {
      const response = await plansAPI.subscribeToPlan(planId);

      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error("Failed to create checkout session");
        setSubscribing(null);
      }
    } catch (error) {
      console.error("Error subscribing to plan:", error);
      const errorMessage =
        error.response?.data?.detail || "Failed to subscribe to plan";
      toast.error(errorMessage);
      setSubscribing(null);
    }
  };

  const isAnyPlanProcessing = subscribing !== null;

  // Color schemes for different plans
  const getPlanColors = (index) => {
    const colorSchemes = [
      {
        bg: "bg-primary bg-gradient",
        border: "border-primary",
        text: "text-primary",
        button: "btn-primary",
      },
      {
        bg: "bg-success bg-gradient",
        border: "border-success",
        text: "text-success",
        button: "btn-success",
      },
      {
        bg: "bg-info bg-gradient",
        border: "border-info",
        text: "text-info",
        button: "btn-info",
      },
      {
        bg: "bg-warning bg-gradient",
        border: "border-warning",
        text: "text-warning",
        button: "btn-warning",
      },
      {
        bg: "bg-danger bg-gradient",
        border: "border-danger",
        text: "text-danger",
        button: "btn-danger",
      },
    ];
    return colorSchemes[index % colorSchemes.length];
  };

  if (loading) {
    return (
      <div className="min-vh-100 bg-light d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-light">
      <div className="container">
        {/* Header Section */}
        <div className="text-center mb-5">
          <h1 className="display-4 fw-bold text-dark mb-3">Choose Your Plan</h1>
          <p className="lead text-muted mx-auto" style={{ maxWidth: "600px" }}>
            Select a plan to get more credits and continue using our powerful
            SEO and keyword research tools
          </p>
        </div>

        {/* Plans Grid */}
        <div className="row g-4 mb-5 justify-content-center">
          {plans.length > 0 ? (
            plans.map((plan, index) => {
              const colors = getPlanColors(index);
              return (
                <div
                  key={plan.id}
                  className="col-xl-4 col-lg-6 col-md-6 col-12"
                >
                  <div
                    className={`card h-100 border-3 ${
                      colors.border
                    } shadow-lg hover-shadow transition-all ${
                      isAnyPlanProcessing
                        ? subscribing === plan.id
                          ? "border-primary"
                          : "opacity-50"
                        : ""
                    }`}
                  >
                    <div className={`card-body ${colors.bg} text-center p-4`}>
                      <h3 className="card-title h3 fw-bold text-dark mb-4">
                        {plan.name}
                      </h3>

                      <div className="mb-4">
                        <span className={`display-4 fw-bold ${colors.text}`}>
                          ${plan.price}
                        </span>
                        <span className="text-muted ms-2 fs-5">AUD</span>
                      </div>

                      <div className="h4 fw-semibold text-dark mb-4">
                        {plan.credits} Credits
                      </div>

                      <p className="text-muted mb-5 fs-6">{plan.description}</p>

                      {subscribing === plan.id && (
                        <div className="mb-4">
                          <div className="d-flex align-items-center justify-content-center">
                            <div
                              className="spinner-border spinner-border-sm text-primary me-2"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <span className="text-primary small">
                              Processing...
                            </span>
                          </div>
                        </div>
                      )}

                      <button
                        onClick={() => handleSubscribe(plan.id)}
                        disabled={isAnyPlanProcessing}
                        className={`btn ${colors.button} w-100 py-3 ${
                          isAnyPlanProcessing ? "disabled" : ""
                        }`}
                      >
                        {subscribing === plan.id
                          ? "Processing..."
                          : isAnyPlanProcessing
                          ? "Please wait..."
                          : plan.billing_interval ? "Subscription" : "Pay as You Go"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-12 text-center py-5">
              <div className="card border">
                <div className="card-body p-5">
                  <h3 className="h4 fw-semibold text-dark mb-3">
                    No Plans Available
                  </h3>
                  <p className="text-muted">
                    Please check back later for available subscription plans.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="row justify-content-center">
          <div className="col-xl-8 col-lg-10">
            <div className="card bg-primary bg-gradient text-white text-center border-0 shadow">
              <div className="card-body p-5">
                <h3 className="h2 fw-bold mb-3">🎉 Free Trial Credits</h3>
                <p className="lead mb-0">
                  Every new user gets <strong>5 free credits</strong> to start!
                  Use them to try our services before subscribing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Plans;
